import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/game';
import { getSettings, saveSettings, GameSettings } from '../systems/Settings';
import { getSoundSystem } from '../systems/SoundSystem';
import { getMusicPlayer } from '../systems/MusicPlayer';
import { BackHandler, pushSceneState } from '../systems/BackButton';

export class SettingsScene extends Phaser.Scene implements BackHandler {
  private returnTo = 'TitleScene';
  private settings!: GameSettings;

  constructor() {
    super('SettingsScene');
  }

  handleBack(): void {
    this.close();
  }

  init(data: { returnTo?: string }): void {
    this.returnTo = data.returnTo ?? 'TitleScene';
    this.settings = getSettings();
  }

  create(): void {
    pushSceneState();
    getMusicPlayer().startMenu();

    const centerX = GAME_CONFIG.width / 2;

    this.add.text(centerX, 40, 'Settings', {
      fontSize: '24px',
      color: '#a8e6cf',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    const closeBtn = this.add.text(GAME_CONFIG.width - 28, 30, '✕', {
      fontSize: '28px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();

    closeBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.close();
    });

    this.createToggle(centerX, 200, 'Sound Effects', this.settings.sfxEnabled, (value) => {
      this.settings.sfxEnabled = value;
      saveSettings(this.settings);
    });

    this.createToggle(centerX, 320, 'Music', this.settings.musicEnabled, (value) => {
      this.settings.musicEnabled = value;
      saveSettings(this.settings);
      getMusicPlayer().onSettingsChanged();
    });
  }

  private createToggle(
    centerX: number,
    y: number,
    label: string,
    initialValue: boolean,
    onChange: (value: boolean) => void,
  ): void {
    this.add.text(centerX, y - 40, label, {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    const trackWidth = 60;
    const trackHeight = 32;
    const knobRadius = 12;

    const track = this.add.graphics();
    const knob = this.add.graphics();

    let enabled = initialValue;

    const getKnobX = (on: boolean): number =>
      on ? centerX + trackWidth / 2 - knobRadius - 4
        : centerX - trackWidth / 2 + knobRadius + 4;

    const drawTrack = (): void => {
      track.clear();
      track.fillStyle(enabled ? 0x6bcb77 : 0x444466, 1);
      track.fillRoundedRect(
        centerX - trackWidth / 2,
        y - trackHeight / 2,
        trackWidth,
        trackHeight,
        trackHeight / 2,
      );
    };

    const drawKnob = (knobX: number): void => {
      knob.clear();
      knob.fillStyle(0xffffff, 1);
      knob.fillCircle(knobX, y, knobRadius);
    };

    drawTrack();
    drawKnob(getKnobX(enabled));

    const hitArea = this.add.rectangle(centerX, y, trackWidth + 20, trackHeight + 20);
    hitArea.setInteractive();
    hitArea.on('pointerdown', () => {
      enabled = !enabled;
      drawTrack();

      const startX = getKnobX(!enabled);
      const endX = getKnobX(enabled);

      this.tweens.addCounter({
        from: startX,
        to: endX,
        duration: 120,
        ease: 'Cubic.easeOut',
        onUpdate: (tween) => {
          drawKnob(tween.getValue() as number);
        },
      });

      onChange(enabled);
    });
  }

  private close(): void {
    if (this.returnTo === 'GameScene') {
      this.scene.stop();
      this.scene.wake('GameScene');
    } else {
      this.scene.start(this.returnTo);
    }
  }
}
